import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ShieldAlert, DollarSign, Mail, Inbox, CheckCheck, Wrench, TrendingDown, CalendarDays, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const TYPE = {
  cash_to_pocket: { label: "Cash to pocket", icon: DollarSign, color: "#E4002B", tint: "bg-red-500/10 border-red-500/40 text-red-400" },
  tamper: { label: "Seat tamper", icon: Wrench, color: "#a21caf", tint: "bg-fuchsia-500/10 border-fuchsia-500/40 text-fuchsia-400" },
  leakage: { label: "Leakage", icon: TrendingDown, color: "#c2410c", tint: "bg-orange-500/10 border-orange-500/40 text-orange-400" },
  recap: { label: "Weekly recap", icon: CalendarDays, color: "#06B6D4", tint: "bg-[#232C40] border-[#2A3550] text-slate-300" },
};

export default function Alerts() {
  const { money } = useBranding();
  const qc = useQueryClient();
  const [clip, setClip] = useState(null);

  const { data } = useQuery({
    queryKey: ["alerts"],
    queryFn: () => api.get("/alerts").then((r) => r.data),
  });
  const alerts = data?.alerts || [];
  const unread = data?.unread || 0;

  const markRead = async (id) => {
    await api.put(`/alerts/${id}/read`);
    qc.invalidateQueries({ queryKey: ["alerts"] });
  };

  const markAll = async () => {
    await api.post("/alerts/read-all");
    qc.invalidateQueries({ queryKey: ["alerts"] });
    toast.success("All alerts marked read");
  };

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Alerts Inbox</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
            What needs a human right now
          </h1>
          <p className="mt-2 text-sm text-slate-400 max-w-2xl">
            Escalated cash-to-pocket flags and seat-tamper detections. Cash-to-pocket alerts are also emailed
            directly to the store owner or manager at end of day.
          </p>
        </div>
        <button
          onClick={markAll}
          disabled={unread === 0}
          data-testid="mark-all-read-btn"
          className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#334063] text-slate-100 transition-colors duration-200 disabled:opacity-50"
        >
          <CheckCheck className="h-4 w-4" /> Mark all read
        </button>
      </div>

      <div className="mt-8 space-y-3" data-testid="alerts-list">
        {alerts.map((a, i) => {
          const t = TYPE[a.type] || TYPE.tamper;
          const Icon = t.icon;
          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              data-testid={`alert-${a.id}`}
              className={`surface-card p-5 flex items-start gap-4 ${a.read ? "opacity-70" : ""}`}
            >
              <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${t.color}14` }}>
                <Icon className="h-5 w-5" style={{ color: t.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded border ${t.tint}`}>{t.label}</span>
                  {!a.read && <span className="h-2 w-2 rounded-full pulse-status" style={{ backgroundColor: t.color }} />}
                  <span className="text-xs text-slate-400">{fmtTime(a.created_at)}</span>
                </div>
                <div className="mt-1.5 text-sm text-slate-100">{a.message}</div>
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                  <span>Store #{a.store_number} — {a.store_name}</span>
                  {a.chair_label && <span>· {a.chair_label}</span>}
                  {a.amount != null && <span className="font-medium" style={{ color: t.color }}>· {money(a.amount)}</span>}
                  <span className="inline-flex items-center gap-1"><Inbox className="h-3 w-3" /> In-app</span>
                  {a.channels?.email && <span className="inline-flex items-center gap-1 text-emerald-400"><Mail className="h-3 w-3" /> Emailed {a.recipient}</span>}
                  {a.clip && <button onClick={() => setClip(a)} data-testid={`view-footage-${a.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-white font-medium"><Play className="h-3 w-3" /> View footage</button>}
                </div>
              </div>
              {!a.read && (
                <button
                  onClick={() => markRead(a.id)}
                  data-testid={`mark-read-${a.id}`}
                  className="text-xs font-medium text-slate-400 hover:text-white transition-colors duration-200 shrink-0"
                >
                  Mark read
                </button>
              )}
            </motion.div>
          );
        })}
        {alerts.length === 0 && (
          <div className="surface-card p-12 text-center">
            <ShieldAlert className="h-8 w-8 mx-auto text-slate-600" />
            <div className="mt-3 text-sm text-slate-400">No alerts. Every seat and every dollar is accounted for.</div>
          </div>
        )}
      </div>

      <Dialog open={!!clip} onOpenChange={(v) => !v && setClip(null)}>
        <DialogContent className="max-w-2xl p-0 bg-black border-zinc-800 overflow-hidden" data-testid="alert-footage-modal">
          <DialogTitle className="sr-only">Video Protocol footage</DialogTitle>
          {clip?.clip && (
            <div className="relative">
              <video src={clip.clip.video_url} poster={clip.clip.poster} controls autoPlay muted playsInline className="w-full block" />
              <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest text-red-400 font-semibold bg-black/60 px-2 py-1 rounded">● REC · face-blurred</div>
              <div className="p-4 bg-[#1E2638] text-sm text-slate-300">{clip.message}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
